<div class="max-w-6xl mx-auto p-6 space-y-8 animate-in fade-in duration-500">
  <!-- Header -->
  <div class="flex items-center justify-between">
    <div>
      <h1 class="text-3xl font-extrabold tracking-tight text-base-content">
        Assign Products to Store
      </h1>
      <p class="text-base-content/60 mt-1">
        Map global product variants to specific stores with store pricing and availability. New assignments start with zero stock.
      </p>
    </div>
    <a routerLink="/inventory/store-products" class="btn btn-ghost btn-sm gap-2">
      <i class="fa-solid fa-arrow-left"></i> Back to Inventory
    </a>
  </div>

  <!-- Step 1 & 2: Selection -->
  <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
    <!-- Store Selection -->
    <div class="card bg-base-100 border border-base-200 shadow-sm rounded-3xl">
      <div class="card-body">
        <label class="label" for="storeSelect">
          <span class="label-text font-bold">1. Select Store</span>
        </label>
        <select
          id="storeSelect"
          [(ngModel)]="selectedStoreId"
          [disabled]="isSaving()"
          (change)="loadAssignments()"
          class="select select-bordered w-full rounded-2xl"
        >
          <option [ngValue]="null" disabled selected>Choose a store</option>
          @for (store of stores(); track store.id) {
          <option [ngValue]="store.id">
            {{ store.name }}
            @if (store.store_code) { ({{ store.store_code }}) }
            @if (store.city) { · {{ store.city }} }
          </option>
          }
        </select>
        <p class="text-xs text-base-content/40 mt-2">
          Choose which store you want to manage inventory for.
        </p>
      </div>
    </div>

    <!-- Product Selection -->
    <div class="card bg-base-100 border border-base-200 shadow-sm rounded-3xl">
      <div class="card-body">
        <label class="label" for="productSearch">
          <span class="label-text font-bold">2. Search Global Product</span>
        </label>
        <div class="relative">
          <input
            id="productSearch"
            type="text"
            [(ngModel)]="querySearch"
            [disabled]="isSaving()"
            (input)="onSearchProducts()"
            placeholder="Search by name..."
            class="input input-bordered w-full rounded-2xl pl-10"
          />
          <i class="fa-solid fa-search absolute left-4 top-1/2 -translate-y-1/2 opacity-40"></i>
        </div>

        <!-- Search Results Dropdown-like -->
        @if (products().length > 0) {
        <div class="mt-2 max-h-40 overflow-y-auto border border-base-200 rounded-xl bg-base-100 shadow-lg">
          @for (prod of products(); track prod.id) {
          <button
            (click)="selectProduct(prod)"
            [disabled]="isSaving()"
            class="w-full flex items-center gap-3 text-left px-4 py-2 hover:bg-base-200 transition-colors text-sm border-b border-base-100 last:border-none"
            [class.bg-primary/5]="selectedProductId === prod.id"
          >
            <span class="relative flex items-center justify-center size-10 shrink-0 rounded-lg bg-base-200 overflow-hidden">
              <i class="fa-solid fa-box text-base-content/30" aria-hidden="true"></i>
              @if (productImage(prod); as imageUrl) {
              <img [src]="imageUrl" alt="" loading="lazy" (error)="hideBrokenImage($event)" class="absolute inset-0 size-full object-contain bg-base-100" />
              }
            </span>
            <span class="min-w-0 break-words">{{ prod.name }}</span>
          </button>
          }
        </div>
        }
        @if (selectedProduct(); as product) {
        <div class="mt-3 flex items-center gap-3 rounded-2xl border border-primary/20 bg-primary/5 p-3" aria-live="polite">
          <div class="relative flex items-center justify-center size-16 shrink-0 rounded-xl bg-base-200 overflow-hidden">
            <i class="fa-solid fa-box text-xl text-base-content/30" aria-hidden="true"></i>
            @if (productImage(product); as imageUrl) {
            <img [src]="imageUrl" [alt]="product.name" (error)="hideBrokenImage($event)" class="absolute inset-0 size-full object-contain bg-base-100" />
            }
          </div>
          <div class="min-w-0">
            <p class="text-xs text-base-content/60">Selected product</p>
            <p class="font-bold break-words">{{ product.name }}</p>
          </div>
        </div>
        }
      </div>
    </div>
  </div>

  <!-- Step 3: Assignments Table -->
  @if (selectedStoreId && selectedProductId) {
  <div class="card bg-base-100 border border-base-200 shadow-xl rounded-3xl overflow-hidden">
    <div class="card-body p-0">
      <div class="p-6 border-b border-base-200 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <h3 class="font-bold text-lg flex items-center gap-2">
          <i class="fa-solid fa-list-check text-primary"></i>
          Product Variants
        </h3>
        
        <div class="flex flex-wrap gap-2">
          <button (click)="applyPriceToAll()" [disabled]="isSaving() || isLoading()" class="btn btn-ghost btn-xs text-primary bg-primary/5 hover:bg-primary/10">
            Apply Price to All
          </button>
        </div>
      </div>

      <div class="overflow-x-auto">
        <table class="table table-zebra">
          <thead>
            <tr class="bg-base-200/50">
              <th class="w-12 text-center">
                <input type="checkbox" class="checkbox checkbox-sm checkbox-primary" (change)="toggleAll($event)" [disabled]="isSaving() || isLoading()">
              </th>
              <th>Variant Info</th>
              <th>MRP</th>
              <th>Selling Price</th>
              <th>Current Stock</th>
              <th>Low stock</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            @if (isLoading()) {
            <tr class="animate-pulse">
              <td colspan="7" class="text-center py-12">
                <span class="loading loading-spinner text-primary"></span>
                <p class="text-sm opacity-50 mt-2">Loading assignments...</p>
              </td>
            </tr>
            }
            
            @for (item of assignments(); track item.variant_id; let i = $index) {
            <tr>
              <td class="text-center">
                <input type="checkbox" [(ngModel)]="item.isAssigned" [disabled]="isSaving() || isLoading()" class="checkbox checkbox-sm checkbox-primary">
              </td>
              <td>
                <div class="font-bold">{{ item.variant_name }}</div>
                <div class="text-xs opacity-50">ID: {{ item.variant_id }}</div>
              </td>
              <td class="font-mono text-sm">₹{{ item.mrp }}</td>
              <td>
                <div class="join join-horizontal">
                  <span class="join-item btn btn-xs btn-ghost border border-base-300 pointer-events-none">₹</span>
                  <input
                    type="number"
                    [(ngModel)]="item.price"
                    [disabled]="!item.isAssigned || isSaving() || isLoading()"
                    class="join-item input input-xs input-bordered w-24 focus:input-primary"
                  />
                </div>
              </td>
              <td>
                <span class="font-mono">{{ item.stock }}</span>
              </td>
              <td>
                <input
                  type="number"
                  min="0"
                  [(ngModel)]="item.low_stock_threshold"
                  [disabled]="!item.isAssigned || isSaving() || isLoading()"
                  class="input input-xs input-bordered w-16 focus:input-primary"
                />
              </td>
              <td>
                <div class="flex items-center gap-2">
                  <input
                    type="checkbox"
                    [(ngModel)]="item.is_available"
                    [disabled]="!item.isAssigned || isSaving() || isLoading()"
                    class="toggle toggle-primary toggle-sm"
                  />
                  <span class="text-xs font-medium" [class.text-primary]="item.is_available" [class.opacity-40]="!item.is_available">
                    {{ item.is_available ? 'Available' : 'Unavailable' }}
                  </span>
                </div>
              </td>
            </tr>
            }
          </tbody>
        </table>
      </div>

      <p class="px-6 py-3 text-sm text-base-content/60">Uncheck a variant and save to remove its store assignment (superadmin only). Stock must be zero and inventory/order history may prevent removal; turn off Available to stop sales instead. Current stock is read-only here; use the stock editor with a reason to change stock.</p>
      <!-- Save Footer -->
      <div class="p-6 bg-base-200/30 flex justify-end gap-3 border-t border-base-200">
        <button (click)="loadAssignments()" class="btn btn-ghost rounded-2xl px-6" [disabled]="isSaving()">
          Reset
        </button>
        <button (click)="saveAssignments()" class="btn btn-primary rounded-2xl px-8 shadow-lg shadow-primary/30" [disabled]="isSaving() || isLoading()">
          @if (isSaving()) {
          <span class="loading loading-spinner loading-sm"></span>
          }
          Save Assignments
        </button>
      </div>
    </div>
  </div>
  }

  <!-- Empty State -->
  @if (!selectedStoreId || !selectedProductId) {
  <div class="flex flex-col items-center justify-center py-20 bg-base-200/20 border-2 border-dashed border-base-300 rounded-3xl">
    <i class="fa-solid fa-boxes-stacked text-5xl opacity-10 mb-4"></i>
    <p class="text-base-content/40 font-medium">Please select a store and product to start mapping inventory.</p>
  </div>
  }
</div>
